import { useEffect } from "react";
import { Link } from "react-router-dom";
import { applyPageSeo } from "../../lib/seo";
import { RelatedArticles } from "../../components/RelatedArticles";
import { TableOfContents } from "../../components/TableOfContents";
import { FloatingTableOfContents } from "../../components/FloatingTableOfContents";

const aiSteps = [
  {
    step: "1",
    title: "Describe your email",
    description:
      "Write a plain-language prompt like 'Create a welcome email for a project management SaaS with a clear activation CTA.' Be specific about audience, tone, and purpose — the AI handles layout, copy, and styling.",
    tip: "Include the email type (welcome, receipt, alert), your product category, and the primary action you want the user to take.",
  },
  {
    step: "2",
    title: "AI generates the template",
    description:
      "Gemini AI produces a complete React Email component with proper structure: header, body sections, CTA, footer. The code uses @react-email/components and renders a real, responsive email — not a wireframe.",
    tip: "The AI generates valid React Email JSX that you can immediately export or customize. No drag-and-drop, no visual editor fighting — just clean code.",
  },
  {
    step: "3",
    title: "Preview and iterate",
    description:
      "See the generated email rendered live in the builder. Toggle between desktop and mobile preview. Edit the code directly to tweak copy, colors, or layout. Regenerate if you want a different direction.",
    tip: "You can edit the generated code like any React component. Change brand colors, swap sections, adjust spacing — it's just JSX.",
  },
  {
    step: "4",
    title: "Export and ship",
    description:
      "Export as production-ready HTML for any ESP, or as React Email JSX for your codebase. Copy the JSX to clipboard for instant integration. Both formats are tested and render consistently across email clients.",
    tip: "React Email components render to HTML that works in Gmail, Outlook, Apple Mail, and every major client. No email client hacks needed.",
  },
];

const emailTypes = [
  {
    type: "Onboarding & Welcome",
    examples: ["Welcome email", "Getting started guide", "Feature walkthrough", "Account activation"],
    prompt: 'Create a welcome email for [product] that introduces 3 key features and includes a "Get Started" CTA button',
  },
  {
    type: "Transactional",
    examples: ["Payment receipt", "Order confirmation", "Shipping update", "Booking confirmation"],
    prompt: 'Create a payment receipt email for [product] showing the amount, date, and order details with a "View Receipt" link',
  },
  {
    type: "Security & Auth",
    examples: ["Password reset", "2FA code", "Login alert", "Account verification"],
    prompt: 'Create a password reset email with a clear "Reset Password" button and a note about the 24-hour expiry',
  },
  {
    type: "Retention & Re-engagement",
    examples: ["Streak reminder", "Weekly digest", "Win-back offer", "Milestone celebration"],
    prompt: 'Create a weekly activity digest for [product] showing 3 key metrics and a "Continue" CTA',
  },
  {
    type: "Marketing & Promotions",
    examples: ["Product launch", "Discount code", "Event invitation", "Newsletter"],
    prompt: 'Create a product launch announcement email for [product] with a hero section, feature highlights, and early-access CTA',
  },
  {
    type: "Collaboration",
    examples: ["Team invite", "Document shared", "Comment notification", "Approval request"],
    prompt: 'Create a team invitation email for [workspace] with the inviter name, workspace details, and "Accept Invite" button',
  },
];

const comparisonTable = [
  { approach: "Hand-code from scratch", speed: "2-4 hours", quality: "High (with effort)", consistency: "Low", reusability: "Low" },
  { approach: "Drag-and-drop builder", speed: "30-60 min", quality: "Medium", consistency: "Medium", reusability: "Low" },
  { approach: "Copy-paste from old emails", speed: "15-30 min", quality: "Low-Medium", consistency: "Low", reusability: "None" },
  { approach: "React Email Builder AI", speed: "30-60 seconds", quality: "High", consistency: "High", reusability: "High" },
];

const blogTocItems = [
  { id: "ai-workflow", text: "The AI email workflow in 4 steps", level: 2 },
  { id: "prompt-examples", text: "AI prompt examples for every email type", level: 2 },
  { id: "speed-comparison", text: "How fast is AI email generation?", level: 2 },
  { id: "faq", text: "Frequently asked questions", level: 2 },
];

export function BuildEmailTemplatesWithAI() {
  useEffect(() => {
    const cleanup = applyPageSeo({
      title: "Build Email Templates with AI — Step-by-Step Guide",
      description:
        "Step-by-step guide to building production-ready email templates with AI. Generate React Email components from a prompt, preview live, and export HTML or JSX. Free tier available.",
      path: "/blog/build-email-templates-with-ai",
      keywords: [
        "build email templates with ai",
        "ai email template generator",
        "generate email templates ai",
        "react email ai builder",
        "email template generator",
        "ai email builder",
        "automated email design",
      ],
    });

    // Add Article structured data for Google rich results
    const articleJsonLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "How to Build Email Templates with AI — React Email Builder Guide",
      "description": "Step-by-step guide to building production-ready email templates with AI. Generate React Email components from a prompt, preview live, and export HTML or JSX.",
      "url": "https://reactemailbuilder.com/blog/build-email-templates-with-ai",
      "datePublished": "2026-04-05",
      "dateModified": "2026-04-19",
      "author": { "@type": "Organization", "name": "React Email Builder" },
      "publisher": { "@type": "Organization", "name": "React Email Builder", "url": "https://reactemailbuilder.com" },
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(articleJsonLd);
    script.id = "article-jsonld";
    document.head.appendChild(script);

    return () => {
      cleanup();
      const existing = document.getElementById("article-jsonld");
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="bg-white text-slate-950">
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="mb-4 flex items-center gap-2 text-sm">
            <Link to="/" className="text-slate-400 transition hover:text-slate-600">Home</Link>
            <svg aria-hidden="true" className="h-3.5 w-3.5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            <Link to="/blog" className="text-slate-400 transition hover:text-slate-600">Blog</Link>
            <svg aria-hidden="true" className="h-3.5 w-3.5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            <span className="text-slate-700">Build Email Templates with AI</span>
          </nav>
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-sky-700">
            How-to Guide
          </p>
          <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
            How to Build Email Templates with AI
          </h1>
          <p className="mt-6 text-lg leading-8 text-slate-600 sm:text-xl">
            From a plain-language prompt to a production-ready email template in under 60 seconds.
            Here's the complete workflow using React Email Builder's AI generation.
          </p>
          <div className="mt-8">
            <Link
              to="/builder"
              className="inline-flex items-center gap-2 rounded bg-slate-950 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              Try AI generation free
            </Link>
          </div>
        </div>
      </section>

      {/* 4-Step Workflow */}
      <section className="py-14 sm:py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="ai-workflow" className="text-3xl font-bold tracking-tight sm:text-4xl">
            The AI email workflow in 4 steps
          </h2>

          <div className="mt-10 space-y-8">
            {aiSteps.map((s) => (
              <div key={s.step} className="rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-sky-100 text-lg font-bold text-sky-700">
                    {s.step}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-slate-950">{s.title}</h3>
                    <p className="mt-3 text-base leading-7 text-slate-600">{s.description}</p>
                    <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 px-4 py-3">
                      <p className="text-sm text-amber-900">
                        <span className="font-semibold">💡 Pro tip:</span> {s.tip}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile TOC — shown below intro on mobile, hidden on xl+ where floating TOC appears */}
          <div className="mt-10 xl:hidden">
            <TableOfContents items={blogTocItems} />
          </div>
        </div>
      </section>

      <FloatingTableOfContents items={blogTocItems} />

      {/* Mid-content CTA */}
      <section className="border-y border-slate-200 bg-slate-50 py-8">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:px-8">
            <div>
              <p className="text-sm font-semibold text-slate-950">
                Ready to try it yourself?
              </p>
              <p className="mt-1 text-sm text-slate-600">
                Free tier: 3 AI generations per day. No credit card required.
              </p>
            </div>
            <Link
              to="/auth/signup?redirect=%2Fbuilder"
              className="shrink-0 rounded bg-slate-950 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              Create free account
            </Link>
          </div>
        </div>
      </section>

      {/* Email Types & Prompt Examples */}
      <section className="border-t border-slate-200 bg-slate-50 py-14 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 id="prompt-examples" className="text-3xl font-bold tracking-tight sm:text-4xl">
            AI prompt examples for every email type
          </h2>
          <p className="mt-4 max-w-2xl text-lg text-slate-600">
            The more specific your prompt, the better the result. Here are starter prompts for the most common email types.
          </p>

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {emailTypes.map((et) => (
              <div key={et.type} className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-950">{et.type}</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {et.examples.map((ex) => (
                    <span key={ex} className="rounded bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                      {ex}
                    </span>
                  ))}
                </div>
                <div className="mt-4 rounded-md bg-slate-950 p-4">
                  <p className="text-sm text-emerald-400 font-mono">{et.prompt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Speed Comparison */}
      <section className="border-t border-slate-200 bg-white py-14 sm:py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="speed-comparison" className="text-3xl font-bold tracking-tight sm:text-4xl">
            How fast is AI email generation?
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Compared to the traditional approaches:
          </p>

          <div className="mt-8 overflow-hidden rounded-md border border-slate-200">
            <div className="grid grid-cols-5 gap-px bg-slate-200 text-sm font-semibold">
              <div className="bg-slate-100 px-4 py-3 text-slate-700">Approach</div>
              <div className="bg-slate-100 px-4 py-3 text-slate-700">Speed</div>
              <div className="bg-slate-100 px-4 py-3 text-slate-700">Quality</div>
              <div className="bg-slate-100 px-4 py-3 text-slate-700">Consistency</div>
              <div className="bg-slate-100 px-4 py-3 text-slate-700">Reusability</div>
            </div>
            {comparisonTable.map((row) => (
              <div
                key={row.approach}
                className={`grid grid-cols-5 gap-px bg-slate-200 text-sm ${
                  row.approach === "React Email Builder AI" ? "bg-sky-50" : "bg-white"
                }`}
              >
                <div className={`px-4 py-3 ${row.approach === "React Email Builder AI" ? "font-semibold text-sky-900" : "text-slate-700"}`}>
                  {row.approach}
                </div>
                <div className="px-4 py-3 text-slate-600">{row.speed}</div>
                <div className="px-4 py-3 text-slate-600">{row.quality}</div>
                <div className="px-4 py-3 text-slate-600">{row.consistency}</div>
                <div className="px-4 py-3 text-slate-600">{row.reusability}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-slate-200 bg-slate-50 py-14 sm:py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq" className="text-3xl font-bold tracking-tight sm:text-4xl">
            Frequently asked questions
          </h2>
          <div className="mt-8 space-y-6">
            {[
              {
                q: "Do AI-generated emails work in Outlook and Gmail?",
                a: "Yes. React Email components render to HTML that's tested across major email clients including Outlook, Gmail, Apple Mail, Yahoo, and more. The AI uses @react-email/components which handle client quirks automatically.",
              },
              {
                q: "Can I edit the AI-generated code?",
                a: "Absolutely. The AI produces standard React Email JSX — you own the code and can edit it however you like. Change colors, swap sections, add conditional logic. It's just React.",
              },
              {
                q: "How many AI generations do I get for free?",
                a: "The free tier includes 3 AI generations per day, 5 exports per month, and 10 saved drafts. No credit card required. A Pro plan with 200 generations/day is coming soon.",
              },
              {
                q: "What AI model powers the generation?",
                a: "We use Google Gemini for email generation. It produces structured React Email JSX with proper component imports, responsive layouts, and brand-appropriate styling.",
              },
              {
                q: "Can I use the exported code in production?",
                a: "Yes. The exported code is production-ready React Email JSX or HTML. Many teams use it directly in their transactional email pipelines with Resend, SendGrid, AWS SES, or any ESP.",
              },
            ].map((faq) => (
              <div key={faq.q} className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="text-base font-semibold text-slate-950">{faq.q}</h3>
                <p className="mt-3 text-base leading-7 text-slate-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedArticles exclude="/blog/build-email-templates-with-ai" />

      {/* CTA */}
      <section className="border-t border-slate-200 bg-slate-950 py-14 text-white sm:py-16">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Build your next email template in 60 seconds
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-slate-300">
            Describe it. AI builds it. Export and ship. Free tier, no credit card required.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <Link
              to="/builder"
              className="inline-flex items-center gap-2 rounded bg-white px-6 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
            >
              Start building
            </Link>
            <Link
              to="/discover/templates"
              className="inline-flex items-center gap-2 rounded border border-white/40 bg-white/10 px-6 py-3 text-sm font-semibold text-white transition hover:border-white/60 hover:bg-white/15"
            >
              Browse templates
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}